<template>
  <div class="HeroInsight" :class="{ 'HeroInsight--static': variant === 'static' }" aria-hidden="true">
    <transition name="ins" mode="out-in">
      <div :key="index" class="HeroInsight__card">
        <div class="HeroInsight__scan"></div>

        <div class="HeroInsight__top">
          <span class="HeroInsight__pulse"></span>
          <span class="HeroInsight__tag">{{ current.tag }}</span>
        </div>

        <div class="HeroInsight__headline" :data-text="current.headline">{{ current.headline }}</div>

        <!-- Counter card (FTP / Readiness): animated number, no chart -->
        <div v-if="current.counter" class="HeroInsight__counterRow">
          <span class="HeroInsight__counter">
            <span v-if="current.counter.dot" class="HeroInsight__counterDot" :style="{ background: current.counter.dot }"></span>
            <span class="HeroInsight__counterVal">
              {{ current.counter.decimals != null ? counterDisplay.toFixed(current.counter.decimals) : Math.round(counterDisplay) }}
              <span class="HeroInsight__counterUnit">{{ current.counter.unit }}</span>
            </span>
          </span>
          <span v-if="current.status" class="HeroInsight__status">{{ current.status }}</span>
          <span v-else class="HeroInsight__delta">
            <svg viewBox="0 0 10 10" class="HeroInsight__arrow"><path d="M5 1 L9 7 H1 Z" /></svg>
            {{ current.delta }}
          </span>
        </div>

        <!-- Chart cards -->
        <template v-else>
          <div class="HeroInsight__metrics">
            <span class="HeroInsight__value">{{ current.value }}</span>
            <span class="HeroInsight__delta">
              <svg viewBox="0 0 10 10" class="HeroInsight__arrow"><path d="M5 1 L9 7 H1 Z" /></svg>
              {{ current.delta }}
            </span>
          </div>

          <div class="HeroInsight__chartWrap">
            <svg class="HeroInsight__chart" viewBox="0 0 240 84" preserveAspectRatio="none">
              <defs>
                <linearGradient :id="`stroke-${variant}-${index}`" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" :stop-color="theme.stroke[0]" />
                  <stop offset="55%" :stop-color="theme.stroke[1]" />
                  <stop offset="100%" :stop-color="theme.stroke[2]" />
                </linearGradient>
                <linearGradient :id="`area-${variant}-${index}`" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" :stop-color="`rgba(${theme.accentRgb}, 0.32)`" />
                  <stop offset="100%" :stop-color="`rgba(${theme.accentRgb}, 0)`" />
                </linearGradient>
                <clipPath :id="`wipe-${variant}-${index}`">
                  <rect class="HeroInsight__wipe" x="0" y="0" width="240" height="84" />
                </clipPath>
              </defs>

              <path :d="current.area" :fill="`url(#area-${variant}-${index})`" :clip-path="`url(#wipe-${variant}-${index})`" />
              <path
                class="HeroInsight__line"
                :d="current.line"
                :stroke="`url(#stroke-${variant}-${index})`"
                :style="{ filter: `drop-shadow(0 0 5px rgba(${theme.accentRgb}, 0.5))` }"
                fill="none"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                pathLength="100"
              />
            </svg>
            <!-- endpoint dot as HTML so it stays a perfect circle (the SVG is x/y stretched) -->
            <span class="HeroInsight__dotWrap" :style="dotStyle">
              <span class="HeroInsight__dotRing" :style="{ borderColor: `rgb(${theme.accentRgb})` }"></span>
              <span class="HeroInsight__dot" :style="{ boxShadow: `0 0 6px rgba(${theme.accentRgb}, 0.95)` }"></span>
            </span>
          </div>
        </template>
      </div>
    </transition>
    <!-- <div class="text-balance opacity-70 max-w-md px-4 mt-4 text-sm text-center">
      Formbeat athletes improved their FTP by
      <span class="font-bold" style="color: #b6ffc9">9%</span>
      in first 3 months.
    </div> -->
  </div>
</template>

<script>
export default {
  props: {
    // 'floating' = absolute card top-right (desktop); 'static' = in-flow card (mobile)
    variant: { type: String, default: 'floating' }
  },

  data() {
    return {
      index: 0,
      timer: null,
      counterDisplay: 0,
      counterRaf: null,
      insights: [
        {
          // Counter card: animated number, no chart
          tag: 'FTP',
          headline: 'New FTP detected',
          counter: { from: 286, to: 298, unit: 'W' },
          delta: '+12 W'
        },
        {
          // Steady climb, denser datapoints (long aerobic base trend)
          tag: 'EFFICIENCY',
          headline: 'Aerobic base rising',
          value: '1.94 W/bpm',
          delta: '+6%',
          line: 'M6 70 L26 68 L46 67 L68 64 L90 61 L112 58 L134 54 L156 49 L178 43 L200 35 L222 27 L234 18',
          area: 'M6 70 L26 68 L46 67 L68 64 L90 61 L112 58 L134 54 L156 49 L178 43 L200 35 L222 27 L234 18 L234 84 L6 84 Z',
          dot: [234, 18]
        },
        {
          // Counter card with status dot, no chart
          tag: 'READINESS',
          headline: 'Recovered and ready',
          counter: { from: 7.4, to: 8.9, unit: '/ 10', decimals: 1, dot: '#5b9dff' },
          status: 'Peak'
        },
        {
          // Accelerating: gentle then steep
          tag: 'FORM',
          headline: 'Fitness trending up',
          value: 'CTL 71',
          delta: '+5 this week',
          line: 'M6 72 L56 71 L104 67 L148 58 L188 42 L234 14',
          area: 'M6 72 L56 71 L104 67 L148 58 L188 42 L234 14 L234 84 L6 84 Z',
          dot: [234, 14]
        },
        {
          // Hockey stick: flat, then a late spike (power record)
          tag: 'SPRINT',
          headline: 'New 5s peak power',
          value: '1,248 W',
          delta: '+42 W',
          line: 'M6 56 L62 55 L118 54 L168 52 L200 47 L222 36 L234 8',
          area: 'M6 56 L62 55 L118 54 L168 52 L200 47 L222 36 L234 8 L234 84 L6 84 Z',
          dot: [234, 8],
          theme: { stroke: ['#6d28d9', '#a855f7', '#e9d5ff'], accentRgb: '192, 132, 252' }
        }
      ]
    }
  },

  computed: {
    current() {
      return this.insights[this.index]
    },
    // Endpoint dot position as a % of the chart box (viewBox is 240 x 84).
    dotStyle() {
      if (!this.current.dot) return {}
      return {
        left: `${(this.current.dot[0] / 240) * 100}%`,
        top: `${(this.current.dot[1] / 84) * 100}%`
      }
    },
    // Per-card chart colours; defaults to the warm→mint scheme.
    theme() {
      return this.current.theme || { stroke: ['#fc4c01', '#ffd27a', '#b6ffc9'], accentRgb: '182, 255, 201' }
    }
  },

  watch: {
    index() {
      this.runCounter()
    }
  },

  created() {
    // SSR-safe seed: requestAnimationFrame isn't available on the server, so just
    // render the start value; the client animates it from mounted().
    if (this.current.counter) {
      this.counterDisplay = this.current.counter.from
    }
  },

  mounted() {
    this.runCounter()
    this.timer = setInterval(() => {
      this.index = (this.index + 1) % this.insights.length
    }, 6500)
  },

  beforeDestroy() {
    clearInterval(this.timer)
    cancelAnimationFrame(this.counterRaf)
  },

  methods: {
    // Count from `from` to `to` for counter cards; restarts each time the card cycles in.
    runCounter() {
      cancelAnimationFrame(this.counterRaf)
      const c = this.current.counter
      if (!c) return

      const duration = 1400
      const start = performance.now()
      this.counterDisplay = c.from

      const step = (now) => {
        const p = Math.min(1, (now - start) / duration)
        const eased = 1 - Math.pow(1 - p, 3) // ease-out cubic
        this.counterDisplay = c.from + (c.to - c.from) * eased // raw; template rounds/toFixes
        if (p < 1) this.counterRaf = requestAnimationFrame(step)
      }
      this.counterRaf = requestAnimationFrame(step)
    }
  }
}
</script>

<style lang="postcss" scoped>
.HeroInsight {
  @apply hidden;
  position: absolute;
  /* ~160px below the header Join button (its bottom sits ~70px down). */
  top: 190px;
  /* Align the right edge with the header's Join button: the 1440px (90rem) centered nav column
     (max(1rem, …) accounts for the header's 1rem side padding below 1440px), plus the secondary
     pill's 0.4rem right padding + 1px border. Keep in sync with .HeaderOverlay in Header.vue. */
  right: calc(max(1rem, (100% - 1440px) / 2) + 0.4rem + 1px);
  z-index: 4;
  width: 19rem;

  @media only screen and (min-width: theme('screens.lg')) {
    @apply block;
  }
  @media only screen and (min-width: theme('screens.xl')) {
    width: 20.5rem;
  }
}

/* Static in-flow variant (mobile, below the CTAs) — overrides the floating layout.
   Shown below lg and hidden at lg+ (where the floating card takes over). */
.HeroInsight--static {
  @apply relative block;
  position: static;
  top: auto;
  right: auto;
  z-index: auto;
  width: 100%;
  /* Constant height so cards of different heights (and the out-in transition gap)
     don't shift surrounding content. */
  min-height: 10rem;
}
/* Embedded (e.g. in the intro bento): the surrounding cell provides the surface,
   so strip the card's own background / border / radius / padding. Center the
   content so shorter cards sit balanced within the constant height. */
.HeroInsight--static .HeroInsight__card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.HeroInsight__card {
  @apply relative px-5 py-4 overflow-hidden text-white;
  background: rgba(20, 20, 19, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 1rem;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 24px 60px -32px rgba(0, 0, 0, 0.85);
}

/* --- header row --- */
.HeroInsight__top {
  @apply flex items-center mb-2;
}
.HeroInsight__pulse {
  @apply inline-block mr-2 rounded-full;
  width: 7px;
  height: 7px;
  background: #b6ffc9;
  box-shadow: 0 0 10px rgba(182, 255, 201, 0.9);
  animation: ins-blink 1.6s ease-in-out infinite;
}
.HeroInsight__tag {
  @apply text-xs font-bold;
  letter-spacing: 0.18em;
  color: rgba(255, 255, 255, 0.65);
}

/* --- headline (glitch reveal) --- */
.HeroInsight__headline {
  @apply relative text-2xl font-bold leading-tight;
  animation: ins-main 0.28s steps(3, end) 0.08s both;
}
.HeroInsight__headline::before,
.HeroInsight__headline::after {
  content: attr(data-text);
  @apply absolute top-0 left-0;
  width: 100%;
  pointer-events: none;
}
.HeroInsight__headline::before {
  color: #fc4c01;
  animation: ins-glitch-a 0.26s steps(2, end) 0.09s both;
}
.HeroInsight__headline::after {
  color: #6ee7a8;
  animation: ins-glitch-b 0.26s steps(2, end) 0.09s both;
}

/* --- metrics --- */
.HeroInsight__metrics {
  @apply flex items-baseline mt-1 mb-3;
  animation: ins-rise 0.45s ease-out 0.34s both;
}
.HeroInsight__value {
  @apply mr-2 text-base font-semibold;
  color: rgba(255, 255, 255, 0.92);
}
.HeroInsight__delta {
  @apply inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded;
  color: #c8ffd8;
  background: rgba(182, 255, 201, 0.14);
}
.HeroInsight__arrow {
  @apply mr-1;
  width: 8px;
  height: 8px;
  fill: #b6ffc9;
}

/* --- counter card (FTP): big animated number, no chart --- */
.HeroInsight__counterRow {
  @apply flex items-center justify-between;
  margin-top: 0.25rem;
  min-height: 64px;
  animation: ins-rise 0.45s ease-out 0.34s both;
}
.HeroInsight__counter {
  @apply inline-flex items-center font-bold;
  gap: 0.18em;
  font-size: 2.6rem;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #fff;
  font-variant-numeric: tabular-nums;
}
.HeroInsight__counterUnit {
  @apply font-semibold;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.55);
}
/* Status dot before the number (e.g. readiness) */
.HeroInsight__counterDot {
  @apply flex-shrink-0 rounded-full;
  width: 0.5em;
  height: 0.5em;
}
/* Status word (e.g. "Peak") in place of the delta chip */
.HeroInsight__status {
  @apply text-sm font-bold;
  color: #5b9dff;
  letter-spacing: 0.01em;
}

/* --- chart --- */
.HeroInsight__chartWrap {
  @apply relative;
}
.HeroInsight__chart {
  @apply block w-full;
  height: 56px;
}
.HeroInsight__line {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  filter: drop-shadow(0 0 5px rgba(255, 210, 122, 0.45));
  animation: ins-draw 1.1s ease-out 0.05s forwards;
}
.HeroInsight__wipe {
  animation: ins-wipe 1.1s ease-out 0.05s both;
}
/* Endpoint dot: HTML overlay (perfect circle, unaffected by the SVG's x/y stretch).
   The wrap is a zero-size anchor positioned at the point; children center on it. */
.HeroInsight__dotWrap {
  position: absolute;
  width: 0;
  height: 0;
}
.HeroInsight__dot {
  position: absolute;
  left: 0;
  top: 0;
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: #ffffff;
  box-shadow: 0 0 6px rgba(182, 255, 201, 0.95);
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
  animation: ins-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 1s both;
}
.HeroInsight__dotRing {
  position: absolute;
  left: 0;
  top: 0;
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  border: 1.5px solid #b6ffc9;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.5);
  animation: ins-ring 1.8s ease-out 1.1s infinite;
}

/* --- card-level scanline --- */
.HeroInsight__scan {
  @apply absolute left-0 right-0;
  pointer-events: none;
  height: 35%;
  top: -35%;
  background: linear-gradient(to bottom, rgba(182, 255, 201, 0), rgba(182, 255, 201, 0.12), rgba(182, 255, 201, 0));
  animation: ins-scan 0.85s ease-in 0.1s both;
}

/* --- enter / leave between insights --- */
.ins-enter-active {
  transition: opacity 0.3s ease;
}
.ins-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease, filter 0.28s ease;
}
.ins-enter {
  opacity: 0;
}
.ins-leave-to {
  opacity: 0;
  transform: translateX(6px) skewX(-3deg);
  filter: blur(1px);
}

/* --- keyframes --- */
@keyframes ins-draw {
  to {
    stroke-dashoffset: 0;
  }
}
@keyframes ins-wipe {
  from {
    width: 0;
  }
  to {
    width: 240px;
  }
}
@keyframes ins-pop {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}
@keyframes ins-ring {
  0% {
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(0.5);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(2.6);
  }
}
@keyframes ins-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
@keyframes ins-scan {
  from {
    top: -35%;
  }
  to {
    top: 135%;
  }
}
@keyframes ins-main {
  0% {
    opacity: 0;
    transform: translate(2px, 0);
  }
  30% {
    opacity: 1;
    transform: translate(-3px, 0);
  }
  60% {
    transform: translate(2px, 0);
  }
  100% {
    opacity: 1;
    transform: translate(0, 0);
  }
}
@keyframes ins-glitch-a {
  0% {
    opacity: 0.9;
    transform: translate(-3px, -1px);
    clip-path: inset(0 0 62% 0);
  }
  25% {
    transform: translate(2px, 1px);
    clip-path: inset(22% 0 48% 0);
  }
  50% {
    transform: translate(-2px, 0);
    clip-path: inset(44% 0 24% 0);
  }
  75% {
    transform: translate(3px, -1px);
    clip-path: inset(12% 0 70% 0);
  }
  100% {
    opacity: 0;
    transform: translate(0, 0);
    clip-path: inset(0 0 100% 0);
  }
}
@keyframes ins-glitch-b {
  0% {
    opacity: 0.9;
    transform: translate(3px, 1px);
    clip-path: inset(58% 0 0 0);
  }
  25% {
    transform: translate(-2px, -1px);
    clip-path: inset(48% 0 20% 0);
  }
  50% {
    transform: translate(2px, 1px);
    clip-path: inset(24% 0 44% 0);
  }
  75% {
    transform: translate(-3px, 0);
    clip-path: inset(70% 0 8% 0);
  }
  100% {
    opacity: 0;
    transform: translate(0, 0);
    clip-path: inset(100% 0 0 0);
  }
}
@keyframes ins-rise {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* --- accessibility: settle instantly --- */
@media (prefers-reduced-motion: reduce) {
  .HeroInsight__line {
    stroke-dashoffset: 0;
    animation: none;
  }
  .HeroInsight__wipe {
    width: 240px;
    animation: none;
  }
  .HeroInsight__dot {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
    animation: none;
  }
  .HeroInsight__dotRing,
  .HeroInsight__scan {
    display: none;
  }
  .HeroInsight__headline,
  .HeroInsight__metrics {
    animation: none;
  }
  .HeroInsight__headline::before,
  .HeroInsight__headline::after {
    display: none;
  }
}
</style>
